/**
 * Lift2Fit — New Block, one panel for lifters and coaches.
 *
 * A block is named, started from something (empty, the current block, or a past
 * one), and given a time (now, a date, or "just save"). The panel only collects
 * those three decisions — `onCreate` owns the write, because the copying,
 * scheduling and activation rules live on the server and the parent screen
 * already knows how to show the result.
 *
 * Copying shares STRUCTURE only: the API deep-copies days and prescriptions into
 * fresh rows, so editing the new block can never rewrite the one it came from.
 */
import { useEffect, useMemo, useState } from 'react';
import type { PlanHistoryEntry } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { PlusIcon, TimerIcon } from '@/lib/icons';
import { Panel, StatusChip } from './components';

export type NewBlockMode = 'empty' | 'copy';
export type NewBlockWhen = 'now' | 'date' | 'later';

export interface NewBlockChoice {
  mode: NewBlockMode;
  name: string;
  /** The block to copy when `mode === 'copy'`; null for an empty block. */
  copyFromPlanId: string | null;
  when: NewBlockWhen;
  /** `YYYY-MM-DD`, only when `when === 'date'`. */
  startOn: string | null;
}

export function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function nameDateSuffix(): string {
  return new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "1 Sep → 28 Sep", "1 Sep → now", or "scheduled for 1 Oct" when it never ran. */
export function blockRangeLabel(entry: PlanHistoryEntry): string {
  if (entry.activatedAt) {
    const start = formatDate(entry.activatedAt);
    if (entry.archivedAt) return `${start} → ${formatDate(entry.archivedAt)}`;
    if (entry.isActive) return `${start} → now`;
    return `started ${start}`;
  }
  if (entry.startsOn && entry.startsOn > utcToday()) {
    return `scheduled for ${formatDate(entry.startsOn)}`;
  }
  return entry.createdAt ? `created ${formatDate(entry.createdAt)}` : 'never started';
}

export function NewBlockPanel({
  current,
  blocks,
  audience,
  busy,
  error,
  onCancel,
  onCreate,
}: {
  current: { planId: string; name: string } | null;
  /** Every block the lifter has, newest first — the copy sources. */
  blocks: readonly PlanHistoryEntry[];
  audience: 'lifter' | 'coach';
  busy: boolean;
  error?: string | null;
  onCancel: () => void;
  onCreate: (choice: NewBlockChoice) => void;
}) {
  const fallbackName = current?.name ?? (audience === 'coach' ? 'Client block' : 'New block');
  const [name, setName] = useState(`${fallbackName} — ${nameDateSuffix()}`);
  const [nameTouched, setNameTouched] = useState(false);
  const [mode, setMode] = useState<NewBlockMode>('copy');
  const [sourceId, setSourceId] = useState(current?.planId ?? blocks[0]?.planId ?? '');
  const [when, setWhen] = useState<NewBlockWhen>('now');
  const [startOn, setStartOn] = useState(utcToday());
  const [validation, setValidation] = useState<string | null>(null);

  // The current block first, then the rest newest-first. The API already sorts
  // by the block's life; this only lifts the running one to the top.
  const copyOptions = useMemo(() => {
    const rest = blocks.filter((block) => block.planId !== current?.planId);
    if (!current) return rest;
    const running = blocks.find((block) => block.planId === current.planId);
    const currentOption: PlanHistoryEntry =
      running ??
      ({
        planId: current.planId,
        name: current.name,
        track: 'general',
        trackLabel: '',
        weeks: 1,
        daysPerWeek: 0,
        isActive: true,
        createdAt: null,
      } satisfies PlanHistoryEntry);
    return [currentOption, ...rest];
  }, [blocks, current]);

  const hasCopySource = copyOptions.length > 0;

  // No block to copy means the only honest start is empty.
  useEffect(() => {
    if (!hasCopySource) setMode('empty');
    else if (mode === 'copy' && sourceId === '') {
      setSourceId(copyOptions[0]?.planId ?? '');
    }
  }, [copyOptions, hasCopySource, mode, sourceId]);

  const chooseSource = (nextId: string) => {
    setSourceId(nextId);
    if (!nameTouched) {
      const source = copyOptions.find((block) => block.planId === nextId);
      if (source) setName(`${source.name} — ${nameDateSuffix()}`);
    }
  };

  const submit = () => {
    const clean = name.trim();
    if (!clean) {
      setValidation('Give the block a name.');
      return;
    }
    if (mode === 'copy' && !sourceId) {
      setValidation('Choose the block to copy, or start empty.');
      return;
    }
    if (when === 'date' && !startOn) {
      setValidation('Pick the date the block should start.');
      return;
    }
    setValidation(null);
    onCreate({
      mode,
      name: clean,
      copyFromPlanId: mode === 'copy' ? sourceId : null,
      when,
      startOn: when === 'date' ? startOn : null,
    });
  };

  return (
    <Panel
      testId="new-block-panel"
      title="New Block"
      subtitle="Name it, start it from a block or from empty, and choose when it begins. You can edit every part of it afterwards."
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Block name</span>
          <input
            type="text"
            value={name}
            data-testid="new-block-name"
            onChange={(event) => {
              setName(event.target.value);
              setNameTouched(true);
            }}
            className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
          />
        </label>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Start from</legend>
          <label className="flex items-start gap-2 border border-hairline bg-page p-3 text-[13px] text-ink">
            <input
              type="radio"
              name="new-block-mode"
              checked={mode === 'empty'}
              data-testid="new-block-mode-empty"
              onChange={() => setMode('empty')}
              className="mt-[2px] h-4 w-4 shrink-0"
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="font-bold">Empty block</span>
              <span className="text-[12px] text-ink-muted">
                Open the builder and write the days yourself.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 border border-hairline bg-page p-3 text-[13px] text-ink">
            <input
              type="radio"
              name="new-block-mode"
              checked={mode === 'copy'}
              data-testid="new-block-mode-copy"
              disabled={!hasCopySource}
              onChange={() => setMode('copy')}
              className="mt-[2px] h-4 w-4 shrink-0"
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="font-bold">Copy a block</span>
              <span className="text-[12px] text-ink-muted">
                Days, exercises and prescriptions are copied into a new block. Editing it never changes the original.
              </span>
            </span>
          </label>
          {mode === 'copy' ? (
            <select
              data-testid="new-block-source"
              value={sourceId}
              onChange={(event) => chooseSource(event.target.value)}
              className="h-11 w-full border border-hairline bg-page px-3 text-[14px] text-ink"
            >
              {copyOptions.map((block) => (
                <option key={block.planId} value={block.planId}>
                  {block.planId === current?.planId ? 'Current — ' : ''}
                  {block.name} · {blockRangeLabel(block)}
                </option>
              ))}
            </select>
          ) : null}
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">When</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {(
              [
                ['now', 'Start now', 'Replaces the running block.'],
                ['date', 'Start on a date', 'Saved for that day; activate when ready.'],
                ['later', 'Save for later', 'Sits in All Programmes.'],
              ] as Array<[NewBlockWhen, string, string]>
            ).map(([value, label, hint]) => (
              <label
                key={value}
                className="flex cursor-pointer flex-col gap-1 border border-hairline bg-page p-3 text-[13px] text-ink"
              >
                <span className="flex items-center gap-2 font-bold">
                  <input
                    type="radio"
                    name="new-block-when"
                    checked={when === value}
                    data-testid={`new-block-when-${value}`}
                    onChange={() => setWhen(value)}
                    className="h-4 w-4 shrink-0"
                  />
                  {value === 'date' ? <TimerIcon className="text-[13px] text-accent" /> : null}
                  {label}
                </span>
                <span className="text-[12px] text-ink-muted">{hint}</span>
              </label>
            ))}
          </div>
          {when === 'date' ? (
            <label className="flex flex-col gap-1.5 sm:w-64">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Start date</span>
              <input
                type="date"
                value={startOn}
                data-testid="new-block-start-on"
                onChange={(event) => setStartOn(event.target.value)}
                className="h-11 border border-hairline bg-page px-3 font-mono text-[14px] text-ink"
              />
            </label>
          ) : null}
        </fieldset>

        {!hasCopySource ? (
          <p className="text-[12px] text-ink-muted">
            There is no earlier block to copy yet, so this one starts empty.
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="new-block-submit"
            disabled={busy}
            onClick={submit}
            className="inline-flex h-11 items-center gap-2 bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
          >
            <PlusIcon className="text-[13px]" /> {busy ? 'Creating…' : mode === 'empty' ? 'Continue' : 'Create block'}
          </button>
          <button
            type="button"
            data-testid="new-block-cancel"
            onClick={onCancel}
            className="inline-flex h-11 items-center border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
          >
            Cancel
          </button>
          {when === 'date' ? <StatusChip tone="info">Scheduled</StatusChip> : null}
        </div>

        {validation ? <p className="text-[12px] font-bold text-danger">{validation}</p> : null}
        {error ? (
          <p className="border-l-2 border-danger bg-page-subtle px-3 py-2 text-[12px] text-danger" data-testid="new-block-error">
            {error}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
